import { store } from '@/store'
import _isEqual from 'lodash/isEqual'
import { ModuleName } from '@/store/modules/createWarehouseReceivingOrder'
import { saveWro } from '@/models/createWarehouseReceivingOrder/commonActions/index'

import { CreateWarehouseReceivingOrderService } from '@/services/createWarehouseReceivingOrder/index'
import { ProductService } from '@/services/product/index'
import { WroDraftShippingConfiguration, WroDraftShippingBoxConfiguration } from '../../../../receiving/draft/shipping'

import { mapProductsToApi } from '@/mappers/receiving/draft/products'
import { initShippingConfigurations } from '@/mappers/receiving/draft/shippingConfiguration'
import { machine } from '@/models/createWarehouseReceivingOrder/create-order-machine'

export const clearProducts = () => {
  const isWroCreated = store.getters[ModuleName + '/isWroCreated']
  if (store._modules.root._children[ModuleName] && !isWroCreated) store.dispatch(ModuleName + '/clearProducts')
  else if (isWroCreated) machine.transition('PRODUCT_ADDED')
}

export const clearProductsForEditWro = () => {
  if (store._modules.root._children[ModuleName]) store.dispatch(ModuleName + '/clearProducts')
}

export const setHasBeenEditedFlag = (val) => () => {
  store.dispatch(ModuleName + '/setSelectedProductsEdited', val)
}

const multiSKUConfigBreakdown = (skuConfigs) => {
  const skuConfigBreakDown = []
  skuConfigs.forEach((x) => {
    const id = x.product.id
    const qty = x.quantityPerBox * x.numberOfBoxes
    const lotNumber = x.product.lotInformation.lotNumber
    const expDate = x.product.lotInformation.expirationDate
    let index = -1
    index = skuConfigBreakDown.findIndex(y => y.id === id && y.lotNumber === lotNumber && y.expDate === expDate)
    index > -1 ? skuConfigBreakDown[index].qty += qty : skuConfigBreakDown.push({ id, qty, lotNumber, expDate })
  })
  return skuConfigBreakDown
}

const singleSKUConfigBreakdown = (skuConfigs) => {
  const skuConfigBreakDown = []
  skuConfigs.forEach((x) => {
    const id = x.productId
    const lotNumber = x.lotNumber
    const expDate = x.expirationDate
    x.boxConfigurations.forEach((y) => {
      const qty = y.quantityPerBox * y.numberOfBoxes
      if (skuConfigBreakDown.some(z => z.id === id && z.lotNumber === lotNumber && z.expDate === expDate)) {
        const index = skuConfigBreakDown.findIndex(z => z.id === id && z.lotNumber === lotNumber && z.expDate === expDate)
        skuConfigBreakDown[index].qty += qty
      } else {
        skuConfigBreakDown.push({ id, qty, lotNumber, expDate })
      }
    })
  })
  return skuConfigBreakDown
}

const removeProductFromMultiSkuConfig = (x, draft) => {
  const boxToBeRemoved = []
  draft.multiSKUShippingConfigurations.forEach((y) => {
    const index = y.skuConfigurations.findIndex(z => z.product.id === x.id)
    if (index > -1) y.skuConfigurations.splice(index, 1)
    if (y.skuConfigurations.length < 1) boxToBeRemoved.push(y.id)
  })
  return boxToBeRemoved
}

const removeProductFromSingleSkuConfig = (x, skuConfigs) => {
  return skuConfigs.findIndex(y => y.productId === x.id && y.lotNumber === x.lotNumber && y.expirationDate === x.expDate)
}

const mapToSingleSKUConfig = (productToAdd, lotInfo, lastIndex) => {
  const boxConfig = new WroDraftShippingBoxConfiguration({
    height: 0,
    length: 0,
    width: 0,
    numberOfBoxes: 0,
    quantityPerBox: 0,
    weight: null
  })
  const singleSkuConfig = new WroDraftShippingConfiguration({
    productName: productToAdd.name,
    productId: productToAdd.id,
    id: lastIndex,
    shipmentQuantity: lotInfo.amount,
    lotNumber: lotInfo.lotNumber,
    expirationDate: lotInfo.expirationDate,
    boxConfigurations: [boxConfig]
  })
  return singleSkuConfig
}

const addMissingProductsToSingleSkuConfig = (selectedProducts, skuConfigurations) => {
  const skuToAdd = []
  let length = skuConfigurations.length
  selectedProducts.forEach((x) => {
    const isThere = skuConfigurations.some(y => y.productId === x.id)
    if (isThere) {
      x.quantitiesToSend.forEach((y) => {
        const qtyIsThere = skuConfigurations.some(z => z.lotNumber === y.lotNumber && z.expirationDate === y.expirationDate)
        if (!qtyIsThere) skuToAdd.push(mapToSingleSKUConfig(x, y, length++))
      })
    } else {
      x.quantitiesToSend.forEach((y) => {
        skuToAdd.push(mapToSingleSKUConfig(x, y, length++))
      })
    }
  })
  if (skuToAdd.length) skuToAdd.flatMap(x => skuConfigurations.push(x))
  return skuConfigurations
}

const updateSKUConfigIfProductsChanged = (draft) => {
  let wroConfigHasBeenEdited = false
  const selectedProducts = draft.products
  const isSingleSKU = draft.skuType.id === 1
  const skuConfigs = isSingleSKU ? draft.shippingConfigurations : draft.multiSKUShippingConfigurations.flatMap(x => x.skuConfigurations)

  // break down products to simple model from skuConfiguration array
  const skuConfigBreakDown = isSingleSKU ? singleSKUConfigBreakdown(skuConfigs) : multiSKUConfigBreakdown(skuConfigs)

  // break down products to simple model from selectedProducts array
  const selectedProductsBreakDown = []
  selectedProducts.forEach((x) => {
    const id = x.id
    x.quantitiesToSend.forEach((y) => {
      const qty = y.amount
      const lotNumber = y.lotNumber
      const expDate = y.expirationDate
      selectedProductsBreakDown.push({ id, qty, lotNumber, expDate })
    })
  })

  // find out which product that got changed and no longer belongs in the old sku configuration
  const productsToRemoveFromConfig = []
  skuConfigBreakDown.forEach((x) => {
    const temp = selectedProductsBreakDown.filter(y => (y.id + y.lotNumber + y.expDate + y.qty) === (x.id + x.lotNumber + x.expDate + x.qty))
    if (temp.length < 1) {
      productsToRemoveFromConfig.push(x)
    }
  })

  // remove the products which doesn't belong in the sku configuration array
  productsToRemoveFromConfig.forEach((x) => {
    const boxToBeRemoved = isSingleSKU ? removeProductFromSingleSkuConfig(x, skuConfigs) : removeProductFromMultiSkuConfig(x, draft)
    if (isSingleSKU && boxToBeRemoved >= 0) {
      draft.shippingConfigurations.splice(boxToBeRemoved, 1)
      // to make sure we don't corrupt the default empty config object.
      if (!draft.shippingConfigurations.length) {
        draft.shippingConfigurations = []
        wroConfigHasBeenEdited = true
      }
    } else if (boxToBeRemoved.length > 0) {
      boxToBeRemoved.forEach(y => draft.multiSKUShippingConfigurations.splice(draft.multiSKUShippingConfigurations.findIndex(z => z.id === y), 1))
    }
  })
  // add missing products to single sku config
  if (isSingleSKU) draft.shippingConfigurations = addMissingProductsToSingleSkuConfig(draft.products, draft.shippingConfigurations)
  else if (draft.multiSKUShippingConfigurations.length < 1) store.dispatch(ModuleName + '/clearMultiSkuShippingConfigurationValues')
  return wroConfigHasBeenEdited
}

const hasProductsChanged = (draftProducts, editWroProducts) => {
  let sortedProductsFromDraft = []
  draftProducts.forEach(x => {
    sortedProductsFromDraft.push({ id: x.id, qtyToSend: x.quantitiesToSend.flatMap(y => { return { amount: y.amount, expirationDate: y.expirationDate, lotNumber: y.lotNumber } }) })
  })
  sortedProductsFromDraft = sortedProductsFromDraft.sort((a, b) => { return a.id - b.id })
  let sortedProductsFromEditWro = []
  editWroProducts.forEach(x => {
    sortedProductsFromEditWro.push({ id: x.id, qtyToSend: x.quantitiesToSend.flatMap(y => { return { amount: y.amount, expirationDate: y.expirationDate, lotNumber: y.lotNumber } }) })
  })
  sortedProductsFromEditWro = sortedProductsFromEditWro.sort((a, b) => { return a.id - b.id })
  return !_isEqual(sortedProductsFromDraft, sortedProductsFromEditWro)
}

export const saveProducts = () => {
  const saveWroCurried = saveWro({ mapper: mapProductsToApi, service: CreateWarehouseReceivingOrderService.updateProducts })
  return async (_state, _draft) => {
    const toggleProductIdsToLot = store.getters[ModuleName + '/toggleProductIdsLot']
    const toggleProductIdsToNotLot = store.getters[ModuleName + '/toggleProductIdsNotLot']
    const toggleProductIdsAreDangerous = store.getters[ModuleName + '/toggleProductIdsAreDangerous']
    const toggleProductIdsAreNotDangerous = store.getters[ModuleName + '/toggleProductIdsAreNotDangerous']
    try {
      const toggleProductToLotRes = await ProductService.toggleProductLot(toggleProductIdsToLot)
      const toggleProductToNotLotRes = await ProductService.toggleProductLot(toggleProductIdsToNotLot)
      const toggleProductIsDangerousRes = await ProductService.toggleProductDangerous(toggleProductIdsAreDangerous)
      const toggleProductIsNotDangerousRes = await ProductService.toggleProductDangerous(toggleProductIdsAreNotDangerous)

      if (toggleProductToLotRes === true) store.dispatch(ModuleName + '/clearToggleProductIdsToLot')
      if (toggleProductToNotLotRes === true) store.dispatch(ModuleName + '/clearToggleProductIdsToNotLot')
      if (toggleProductIsDangerousRes === true) store.dispatch(ModuleName + '/clearToggleProductIdsAreDangerous')
      if (toggleProductIsNotDangerousRes === true) store.dispatch(ModuleName + '/clearToggleProductIdsAreNotDangerous')

      if (toggleProductToLotRes !== false && toggleProductToNotLotRes !== false &&
        toggleProductIsDangerousRes !== false && toggleProductIsNotDangerousRes !== false) {
        const isWroCreated = store.getters[ModuleName + '/isWroCreated']
        const editWro = store.getters[ModuleName + '/editWro']
        let wroConfigHasBeenEdited = false
        if (isWroCreated) {
          const ifProductsChanged = hasProductsChanged(_draft.products, editWro.products)
          wroConfigHasBeenEdited = updateSKUConfigIfProductsChanged(_draft)
          _draft.hasProductsChanged = ifProductsChanged
          _draft.hasBoxConfigsChanged = ifProductsChanged
        }
        const res = await saveWroCurried(_state, _draft)
        if (res.success) {
          const hasBeenEdited = store.state[ModuleName].selectedProductsEdited
          if (hasBeenEdited) {
            const newShippingConfigurations = initShippingConfigurations(store.getters[ModuleName + '/selectedProducts'])
            if (!isWroCreated || wroConfigHasBeenEdited) {
              store.dispatch(ModuleName + '/initShippingConfigurations', newShippingConfigurations)
              store.dispatch(ModuleName + '/clearMultiSkuShippingConfigurationValues')
            }
            machine.visited.delete(4) // remove shipping configuration step from visited set, to reset state
          }
          const furthestCompletedStep = store.getters[ModuleName + '/furthestCompletedStep']
          if (hasBeenEdited && furthestCompletedStep >= 4) {
            store.dispatch(ModuleName + '/setFurthestCompletedStep', 3)
          }

          store.dispatch(ModuleName, '/setFurthestCompletedStep', 2)
        }
      }
    } catch (e) {
      console.log(e)
    }
  }
}
